import { memo, useMemo } from 'react'
import { List, Map } from 'immutable'
import InlineSelect, { InlineSelectLabel } from '@aytm/lib/rfd-ui/components/basic/inline-select'
import { decodeText } from '@aytm/app/stats/lib/helpers'

import useImages from '../states/useImages'
import useModes from '../states/useModes'
import useSelectedItems from '../states/useSelectedItems'

import { CompareItemsProps } from '../types'
import { DEFAULT_MAIN_ANALYSIS_NAME, DEFAULT_SIGTEST_ANALYSIS_NAME } from '../config/CompareItems_const'

import style from './CompareItemsModesBar.module.scss'

export type CompareItemsModesBarProps = {
  enableSigtestAnalysis: CompareItemsProps['enableSigtestAnalysis']
  mainAnalysisName: CompareItemsProps['mainAnalysisName']
  sigtestAnalysisName: CompareItemsProps['sigtestAnalysisName']
  presets: CompareItemsProps['presets']
  attributes: CompareItemsProps['attributes']
  canUseImages: CompareItemsProps['canUseImages']
}

export default memo(
  CompareItemsModesBar,
  (prevProps, nextProps) =>
    prevProps.enableSigtestAnalysis === nextProps.enableSigtestAnalysis &&
    prevProps.mainAnalysisName === nextProps.mainAnalysisName &&
    prevProps.sigtestAnalysisName === nextProps.sigtestAnalysisName &&
    prevProps.presets === nextProps.presets &&
    prevProps.attributes === nextProps.attributes &&
    prevProps.canUseImages === nextProps.canUseImages
)

export function CompareItemsModesBar(props: CompareItemsModesBarProps) {
  const {
    enableSigtestAnalysis,
    mainAnalysisName,
    sigtestAnalysisName,
    presets,
    attributes,
    canUseImages = true
  } = props

  const { analysisMode, setAnalysisMode, fullTexts, setFullTexts } = useModes()
  const { showImages, setShowImages } = useImages()
  const { presetIndex, setPresetIndex, sortByAttributeIndex, setSortByAttribute } = useSelectedItems()

  const fetchAnalysisOptions = useMemo(
    () => () =>
      List([
        Map({
          value: 'main',
          render: mainAnalysisName || DEFAULT_MAIN_ANALYSIS_NAME
        }),
        Map({
          value: 'sigtest',
          render: sigtestAnalysisName || DEFAULT_SIGTEST_ANALYSIS_NAME
        })
      ]),
    [mainAnalysisName, sigtestAnalysisName]
  )
  const analysisText = useMemo(() => {
    switch (analysisMode) {
      case 'sigtest': {
        return sigtestAnalysisName || DEFAULT_SIGTEST_ANALYSIS_NAME
      }
      case 'main': {
        return mainAnalysisName || DEFAULT_MAIN_ANALYSIS_NAME
      }
      default: {
        return ''
      }
    }
  }, [analysisMode, mainAnalysisName, sigtestAnalysisName])

  const fetchPresetOptions = useMemo(
    () => () =>
      List(
        presets
          .map((preset, index) =>
            false === preset?.hidden && preset?.text
              ? Map({
                  value: index,
                  render: () => <div className={style.CompareItemsModesBar__preset__option}>{preset.text}</div>
                })
              : null
          )
          .filter(Boolean)
      ),
    [presets]
  )
  const presetText = useMemo(
    () => (null == presetIndex || null == presets[presetIndex] ? 'custom' : presets[presetIndex].text),
    [presetIndex, presets]
  )

  const fetchSortByAttributeOptions = useMemo(
    () => () =>
      List(
        attributes
          .map((attribute, index) =>
            attribute?.text
              ? Map({
                  value: index,
                  render: () => (
                    <div className={style.CompareItemsModesBar__attributeSort__option}>
                      {decodeText(attribute.text).replace(/<\/?[biu]>/gi, '')}
                    </div>
                  )
                })
              : null
          )
          .filter(Boolean)
      ),
    [attributes]
  )
  const sortByAttributeText = useMemo(() => {
    if (null == sortByAttributeIndex || null == attributes[sortByAttributeIndex]) {
      return 'custom'
    }
    return decodeText(attributes[sortByAttributeIndex].text).replace(/<\/?[biu]>/gi, '')
  }, [sortByAttributeIndex, attributes])

  return (
    <div className={style.CompareItemsModesBar}>
      {enableSigtestAnalysis ? (
        <InlineSelectLabel className={style.CompareItemsModesBar__analysisMode}>
          Analysis:&nbsp;
          <InlineSelect
            fetchOptions={fetchAnalysisOptions}
            onSelect={(option: Map<string, string>) => {
              setAnalysisMode(option.get('value') as CompareItemsProps['analysisMode'])
            }}
            value={analysisMode}
          >
            <b>{analysisText}</b>
          </InlineSelect>
        </InlineSelectLabel>
      ) : null}
      <div className={style.CompareItemsModesBar__spacer} />
      {canUseImages ? (
        <label className={style.CompareItemsModesBar__showImages}>
          <input
            className={style.CompareItemsModesBar__showImages__checkbox}
            type="checkbox"
            checked={showImages}
            onChange={event => void setShowImages(Boolean(event.target.checked))}
          />
          Show images
        </label>
      ) : null}
      <label className={style.CompareItemsModesBar__fullTexts}>
        <input
          className={style.CompareItemsModesBar__fullTexts__checkbox}
          type="checkbox"
          checked={fullTexts}
          onChange={event => void setFullTexts(Boolean(event.target.checked))}
        />
        Full text
      </label>
      <InlineSelectLabel className={style.CompareItemsModesBar__preset}>
        Show:&nbsp;
        <InlineSelect
          fetchOptions={fetchPresetOptions}
          onSelect={(option: Map<string, number>) => {
            setPresetIndex(option.get('value'))
          }}
          value={presetIndex}
        >
          <b>{presetText}</b>
        </InlineSelect>
      </InlineSelectLabel>
      <InlineSelectLabel className={style.CompareItemsModesBar__attributeSort}>
        Rank by:&nbsp;
        <InlineSelect
          fetchOptions={fetchSortByAttributeOptions}
          onSelect={(option: Map<string, number>) => {
            setSortByAttribute(option.get('value'))
          }}
          value={sortByAttributeIndex}
        >
          <b>{sortByAttributeText}</b>
        </InlineSelect>
      </InlineSelectLabel>
    </div>
  )
}
